'use client';

import React from 'react';
import { useTranslation } from 'next-i18next';
import { AudioPermissionState } from '../types';

interface PermissionStatusBannerProps {
  permissionState: AudioPermissionState;
}

/**
 * PermissionStatusBanner
 *
 * Displays a status banner informing the user about the current
 * microphone permission state during the audio interview onboarding flow.
 *
 * - 'idle': Shows a warning that microphone permission is needed.
 * - 'requesting': (Not currently handled visually)
 * - 'granted': Shows success that permission is enabled.
 * - 'denied': Shows error that permission was denied.
 *
 * This helps guide the user through browser permission prompts and
 * troubleshooting before joining the interview room.
 */
export const PermissionStatusBanner: React.FC<PermissionStatusBannerProps> = ({
  permissionState,
}) => {
  const { t } = useTranslation('common');

  if (permissionState === 'idle') {
    return (
      <div className="p-4 mb-4 border rounded-lg border-warning/20 bg-warning/10">
        <p className="mb-2 font-medium text-warning">
          {t('audio-interview.microphone-permission.required')}
        </p>
        <p className="text-sm text-ink-medium">
          {t('audio-interview.microphone-permission.required.description')}
        </p>
      </div>
    );
  }

  if (permissionState === 'denied') {
    return (
      <div className="p-4 mb-4 border rounded-lg border-error/20 bg-error/10">
        <p className="mb-2 font-medium text-error">
          {t('audio-interview.microphone-permission.denied')}
        </p>
        <p className="text-sm text-ink-medium">
          {t('audio-interview.microphone-permission.denied.description')}
        </p>
      </div>
    );
  }

  if (permissionState === 'granted') {
    return (
      <div className="p-4 mb-4 border rounded-lg border-secondary/20 bg-secondary/10">
        <p className="font-medium text-secondary">
          {t('audio-interview.microphone-permission.enabled')}
        </p>
      </div>
    );
  }

  return null;
};

